/**
 * TabBar Component
 *
 * Reusable tab navigation component with primary and secondary tab support.
 * Used for Top Picks / Edge Finder navigation.
 */

import React from 'react';

/**
 * TabBar renders a horizontal tab navigation bar
 *
 * @param {Object} props
 * @param {Array<{id: string, label: string}>} props.tabs - Array of tab objects
 * @param {string} props.activeTab - Currently active tab id
 * @param {function} props.onChange - Callback when tab changes
 * @param {string} props.size - 'sm' | 'md' | 'lg' - Tab size (default: 'md')
 * @param {string} props.variant - 'primary' | 'secondary' - Visual variant (default: 'primary')
 */
export default function TabBar({
  tabs,
  activeTab,
  onChange,
  size = 'md',
  variant = 'primary'
}) {
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg'
  };

  const variantClasses = {
    primary: {
      active: 'bg-accent-blue text-white',
      inactive: 'bg-bg-card hover:bg-bg-card-hover text-text-secondary'
    },
    secondary: {
      active: 'bg-bg-card-hover text-text-primary border-b-2 border-accent-blue',
      inactive: 'bg-transparent text-text-secondary hover:text-text-primary'
    }
  };

  return (
    <div className={`flex ${variant === 'secondary' ? 'gap-1 border-b border-border-primary' : 'gap-2'}`}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onChange(tab.id)}
          className={`
            ${sizeClasses[size]}
            font-medium rounded-lg transition-all
            ${activeTab === tab.id
              ? variantClasses[variant].active
              : variantClasses[variant].inactive
            }
            ${variant === 'secondary' ? 'rounded-b-none' : ''}
          `}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}
